// src/app/services/data-offline.service.ts
// This file implements offline database storage using IndexDB

import { targetSuffix } from '../_util/data-target';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, of, Subject, defer, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Note, Tag, ApiResponse, DbInfo, NoteEditSessionSnapshot } from './data.service';
import { LogService } from '../_service/log.service';
import { UserSettingsDoc } from '../_service/user-settings.service';
import { AuthService } from '../_service/auth.service';
import { ErrorReportingService } from '../_service/error-reporting.service';
import { SpaceContextService } from '../_service/space-context.service';
import { Delta } from 'quill';
import { DEFAULT_NOTE_STATUS } from '../_util/note-status.util';

// Simple IndexedDB wrapper without Dexie dependency
@Injectable({
  providedIn: 'root',
})
export class DataOfflineService {
  // !! See Below - 2nd class in source file!
  //export class IndexedDBWrapper {

  //private dbName = 'NotesDatabase';
  /** Pre-multi-user legacy database name — every account's data lived here before
   * per-account databases were introduced. Only ever read for one-time migration. */
  private readonly legacyDbName = 'NotesDatabase_v5_dev';
  private version = 6;
  private notesStore = 'notes';
  private userSettingsStore = 'user-settings';
  private attachmentsStore = 'attachments';
  private editSessionsStore = 'note_edit_sessions';
  private tagsStore = 'tags';

  private static readonly DEFAULT_ATTACHMENT_MIME = 'application/octet-stream';

  // Each authenticated account gets its own physically separate IndexedDB
  // database — isolation is structural (separate storage), not query-filtered,
  // so a missed filter can never leak one account's notes into another's view.
  private dbConnection: IDBDatabase | null = null;
  private dbConnectionName: string | null = null;
  private dbOpenPromise: Promise<IDBDatabase> | null = null;
  private readonly migratedTargets = new Set<string>();

  constructor(
    private logs: LogService,
    public auth: AuthService, //private userSettings: UserSettingsDoc
    private errorReporter: ErrorReportingService,
    private spaceContext: SpaceContextService,
  ) {}

  /** Database name for whichever account is currently authenticated (or a fixed
   * "anonymous" name while logged out — that database stays empty since nothing
   * is read/written while unauthenticated). */
  private getDbNameForAccount(): string {
    const username = this.auth.getUsername();
    const sanitized = username ? username.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 100) : '';
    // The build's home target keeps the historic name; the other target gets its own database
    // so PROD and DEV notes can never mix (see targetSuffix()).
    return `NotesDatabase_v5_dev${targetSuffix()}__${sanitized || 'anonymous'}`;
  }

  private async openDB(): Promise<IDBDatabase> {
    const targetName = this.getDbNameForAccount();

    if (this.dbConnection && this.dbConnectionName === targetName) {
      return this.dbConnection;
    }

    if (this.dbConnection && this.dbConnectionName !== targetName) {
      // Account changed since the last call — stop using the previous
      // account's connection and open the newly-current account's database.
      this.dbConnection.close();
      this.dbConnection = null;
      this.dbConnectionName = null;
      this.dbOpenPromise = null;
    }

    if (!this.dbOpenPromise) {
      this.dbOpenPromise = this.openNamedDb(targetName).then(async (db) => {
        this.dbConnection = db;
        this.dbConnectionName = targetName;
        await this.migrateLegacyDbIfNeeded(targetName, db);
        return db;
      });
    }
    return this.dbOpenPromise;
  }

  private openNamedDb(name: string): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(name, this.version);

      request.onerror = () => {
        console.error('IndexedDB open error:', request.error);
        this.errorReporter.report({
          message: `IndexedDB failed to open: ${request.error?.message ?? String(request.error)}`,
          error_type: 'IndexedDBOpenError',
        });
        reject(request.error);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        console.log('DB Store Names: ', db.objectStoreNames);

        // ------------------------
        // NOTES
        // ------------------------
        if (!db.objectStoreNames.contains(this.notesStore)) {
          console.log('..creating store notes');
          const store = db.createObjectStore(this.notesStore, { keyPath: '_id' });
          store.createIndex('Title', 'Title', { unique: false });
          store.createIndex('Keywords', 'Keywords', { unique: false });
          store.createIndex('created_on', 'created_on', { unique: false });
        }

        // ------------------------
        // CATEGORIES — removed (v6): the Categories feature was retired from the
        // app; drop the now-unused store for anyone upgrading from an older
        // version that still has it.
        // ------------------------
        if (db.objectStoreNames.contains('categories')) {
          console.log('..dropping obsolete store categories');
          db.deleteObjectStore('categories');
        }

        // ------------------------
        // USER SETTINGS
        // ------------------------
        if (!db.objectStoreNames.contains(this.userSettingsStore)) {
          console.log('..creating store user-settings');
          db.createObjectStore(this.userSettingsStore, { keyPath: '_id' });
        }

        // ------------------------
        // ATTACHMENTS (binary + metadata for offline usage)
        // ------------------------
        if (!db.objectStoreNames.contains(this.attachmentsStore)) {
          console.log('..creating store attachments');
          const store = db.createObjectStore(this.attachmentsStore, { keyPath: '_id' });
          store.createIndex('noteId', 'noteId', { unique: false });
          store.createIndex('filename', 'filename', { unique: false });
        }

        // ------------------------
        // META (NEW – baseline marker)
        // ------------------------
        if (!db.objectStoreNames.contains('meta')) {
          console.log('..creating store meta');
          db.createObjectStore('meta', { keyPath: 'key' });
        }

        // ------------------------
        // TAGS (offline-first cache of the canonical /tags list, so the real
        // tag name/color is still available when the Flask server is offline
        // or not responding, not just whatever is embedded in local notes.)
        // ------------------------
        if (!db.objectStoreNames.contains(this.tagsStore)) {
          console.log('..creating store tags');
          const store = db.createObjectStore(this.tagsStore, { keyPath: '_id' });
          store.createIndex('name', 'name', { unique: false });
        }

        // ------------------------
        // NOTE EDIT SESSIONS (autosave Stage 2 — opening snapshots for
        // "Discard changes since opening…". IndexedDB-only, never synced
        // to the backend; keyed by [noteId, sessionId].)
        // ------------------------
        if (!db.objectStoreNames.contains(this.editSessionsStore)) {
          console.log('..creating store note_edit_sessions');
          const store = db.createObjectStore(this.editSessionsStore, { keyPath: ['noteId', 'sessionId'] });
          store.createIndex('noteId', 'noteId', { unique: false });
        }
      };

      request.onsuccess = () => {
        //console.log('IndexedDB open success');
        resolve(request.result);
      };
    });
  }

  /** One-time best-effort migration: before per-account databases existed, every
   * account's data lived in `legacyDbName`. The first time a given account's own
   * database is opened, if it's empty and the legacy database has data, copy it
   * over once so pre-upgrade offline edits (including anything not yet pushed to
   * the server) aren't silently orphaned. Never attributes legacy data to the
   * "anonymous" (logged-out) database. */
  private async migrateLegacyDbIfNeeded(targetName: string, targetDb: IDBDatabase): Promise<void> {
    if (this.migratedTargets.has(targetName) || targetName.endsWith('__anonymous')) return;
    // The pre-multi-user legacy database belongs to the build's home target only.
    if (targetSuffix() !== '') return;
    this.migratedTargets.add(targetName);

    try {
      if (!('databases' in indexedDB)) return; // best-effort only; skip if unsupported
      const dbs: Array<{ name?: string }> = await (indexedDB as any).databases();
      if (!dbs.some((d) => d.name === this.legacyDbName)) return;

      const existingNotes = await this.getAllFromDb(targetDb, this.notesStore);
      if (existingNotes.length) return; // not a fresh account database — nothing to migrate

      const legacyDb = await this.openNamedDb(this.legacyDbName);
      const storeNames = [
        this.notesStore,
        this.attachmentsStore,
        this.tagsStore,
        this.editSessionsStore,
        this.userSettingsStore,
        'meta',
      ];
      for (const storeName of storeNames) {
        const records = await this.getAllFromDb(legacyDb, storeName);
        await this.putAllIntoDb(targetDb, storeName, records);
      }
      legacyDb.close();
      this.logs.append('synch', `IndexedDB: migrated legacy "${this.legacyDbName}" data into "${targetName}".`);
    } catch (e: any) {
      console.error('Legacy IndexedDB migration failed:', e);
    }
  }

  private getAllFromDb(db: IDBDatabase, storeName: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      if (!db.objectStoreNames.contains(storeName)) {
        resolve([]);
        return;
      }
      const tx = db.transaction([storeName], 'readonly');
      const req = tx.objectStore(storeName).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }

  private putAllIntoDb(db: IDBDatabase, storeName: string, records: any[]): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!db.objectStoreNames.contains(storeName) || !records.length) {
        resolve();
        return;
      }
      const tx = db.transaction([storeName], 'readwrite');
      const store = tx.objectStore(storeName);
      for (const record of records) store.put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  // DROP-IN FUNCTIONS — paste INSIDE DataOfflineService class

  createNote() {
    const now = new Date().toISOString();
    const note: Note = {
      _id: this.generateId(),
      type: 'note',

      Title: '',
      DescriptionDelta: new Delta(),
      DescriptionHtml: '',
      CommentsDelta: new Delta(),
      CommentsHtml: '',
      Keywords: '',
      AiKeywords: '',

      Status: 'Temp',
      NoteType: 'To Do',

      Project: 0,
      ProjectName: '',

      Confidential: false,
      Attachments: [],

      // The currently active Space (tasks/space_core_client.md, Stage 2) — undefined (omitted on
      // the wire) is the server's own default, the caller's PRIVATE Space, so this is exactly
      // today's behavior until a user actually switches Spaces.
      spaceId: undefined, // new notes default to the user's PRIVATE Space (backfilled in the editor; omitted = PRIVATE server-side)

      created_on: new Date().toISOString(),
      last_modified: new Date().toISOString(),
    };
    return of({ status: 'success', data: note });
  }

  createNoteFromTemplate(t: Note) {
    const now = new Date().toISOString();
    const note: Note = {
      ...structuredClone(t),
      _id: this.generateId(),
      type: 'note',
      Status: 'Temp',
      created_on: now,
      last_modified: now,
      syncStatus: 'new',
      syncTime: now,
      // Overrides whatever spaceId the template itself carried: creating a note FROM a template is
      // a new-note-creation event in its own right, governed by the user's current active Space,
      // not by wherever the template happens to live.
      spaceId: undefined, // new notes default to the user's PRIVATE Space (backfilled in the editor; omitted = PRIVATE server-side)
    };

    delete (note as any)._rev;
    delete (note as any).remote_rev;

    if ('updated_on' in (note as any)) {
      (note as any).updated_on = now;
    }

    return defer(() => from(this.saveNote(note, false))).pipe(
      map(() => ({ status: 'success', data: note } as const)),
      catchError((err: any) =>
        of({
          status: 'error',
          error: { code: 0, message: err?.message ?? String(err) },
        } as const),
      ),
    );
  }

  createNoteCopy(source: Note) {
    const now = new Date().toISOString();
    const baseTitle = (source?.Title ?? '').trim();
    const note: Note = {
      ...structuredClone(source),
      _id: this.generateId(),
      type: 'note',
      Title: `Copy of ${baseTitle || 'Untitled note'}`,
      Status: 'Temp',
      ReferenceDate: now,
      created_on: now,
      last_modified: now,
      syncStatus: 'new',
      syncTime: now,
      // Overrides whatever spaceId the source note carried — see createNoteFromTemplate() above
      // for why a copy always lands in the user's current active Space rather than the source's.
      spaceId: undefined, // new notes default to the user's PRIVATE Space (backfilled in the editor; omitted = PRIVATE server-side)
    };

    delete (note as any)._rev;
    delete (note as any).remote_rev;

    if ('updated_on' in (note as any)) {
      (note as any).updated_on = now;
    }

    return defer(() => from(this.saveNote(note, false))).pipe(
      map(() => ({ status: 'success', data: note } as const)),
      catchError((err: any) =>
        of({
          status: 'error',
          error: { code: 0, message: err?.message ?? String(err) },
        } as const),
      ),
    );
  }

  createTemplateFromNote(n: Note, name: string) {
    const now = new Date().toISOString();
    const tpl: Note = {
      ...structuredClone(n),
      _id: this.generateTemplateId(),
      type: 'template',
      Title: name,
      created_on: now,
      last_modified: now,
      syncStatus: 'new',
      syncTime: now,
      //remote_rev: '',
    };

    return defer(() => from(this.saveNote(tpl, false))).pipe(
      map(() => ({ status: 'success', data: tpl } as const)),
      catchError((err: any) =>
        of({
          status: 'error',
          error: { code: 0, message: err?.message ?? String(err) },
        } as const),
      ),
    );
  }

  updateNote(n: Note) {
    const updated: Note = {
      ...n,
      last_modified: new Date().toISOString(),
      syncStatus: 'local',
      syncTime: new Date().toISOString(),
    };

    return of({ status: 'success', data: updated });
  }

  //#############################################################################
  // READ-ONLY OPERATIONS
  //#############################################################################

  async getAllNotes(): Promise<Note[]> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.notesStore], 'readonly');
        const store = transaction.objectStore(this.notesStore);
        const request = store.getAll();

        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          const notes = request.result as Note[];
          // Sort by createdAt descending
          //notes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          resolve(notes);
        };
      });
    } catch (error) {
      console.error('Error getting all notes:', error);
      return [];
    }
  }

  async getAllTagsLocal(): Promise<Tag[]> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.tagsStore], 'readonly');
        const store = transaction.objectStore(this.tagsStore);
        const request = store.getAll();

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result as Tag[]);
      });
    } catch (error) {
      console.error('Error getting all tags:', error);
      return [];
    }
  }

  /**
   * Caches the canonical /tags list locally so the real tag name/color is
   * still available offline-first — when the Flask server is offline or not
   * responding — instead of only whatever tag id/name is embedded in notes.
   */
  async syncTagsFromServer(tags: Tag[]): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.tagsStore], 'readwrite');
        const store = transaction.objectStore(this.tagsStore);

        const clearRequest = store.clear();
        clearRequest.onsuccess = () => {
          let completed = 0;
          const total = tags.length;

          if (total === 0) {
            resolve();
            return;
          }
          tags.forEach((tag) => {
            const addRequest = store.add(tag);
            addRequest.onsuccess = () => {
              completed++;
              if (completed === total) {
                resolve();
              }
            };
            addRequest.onerror = () => reject(addRequest.error);
          });
        };
        clearRequest.onerror = () => reject(clearRequest.error);
      });
    } catch (error) {
      console.error('Error syncing tags to IndexedDB:', error);
      throw error;
    }
  }

  async getNote(id: string): Promise<Note | undefined> {
    //console.log('dataOffline: in getNote()..1');
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.notesStore], 'readonly');
        const store = transaction.objectStore(this.notesStore);
        const request = store.get(id);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result as Note);
      });
    } catch (error) {
      console.error('Error getting note:', error);
      return undefined;
    }
  }

  // ------------------------
  // NOTE EDIT SESSIONS (autosave Stage 2 — opening snapshots)
  // ------------------------

  async saveEditSessionSnapshot(snapshot: NoteEditSessionSnapshot): Promise<void> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.editSessionsStore], 'readwrite');
      transaction.objectStore(this.editSessionsStore).put(snapshot);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getEditSessionSnapshot(noteId: string, sessionId: string): Promise<NoteEditSessionSnapshot | undefined> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.editSessionsStore], 'readonly');
        const request = transaction.objectStore(this.editSessionsStore).get([noteId, sessionId]);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result as NoteEditSessionSnapshot | undefined);
      });
    } catch (error) {
      console.error('Error getting edit session snapshot:', error);
      return undefined;
    }
  }

  async deleteEditSessionSnapshot(noteId: string, sessionId: string): Promise<void> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.editSessionsStore], 'readwrite');
      transaction.objectStore(this.editSessionsStore).delete([noteId, sessionId]);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /** Autosave Stage 3 — reads every opening snapshot, for the startup
   * abandoned-session check. */
  async getAllEditSessionSnapshots(): Promise<NoteEditSessionSnapshot[]> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.editSessionsStore], 'readonly');
        const request = transaction.objectStore(this.editSessionsStore).getAll();
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve((request.result as NoteEditSessionSnapshot[]) ?? []);
      });
    } catch (error) {
      console.error('Error getting all edit session snapshots:', error);
      return [];
    }
  }

  // Local (IndexedDB) read of the current user's settings doc — the offline-first counterpart
  // to saveUserSettings(). Used as a fallback when the remote /api/user-settings fetch fails or
  // is unreachable, so settings written locally (e.g. favoriteNoteIds) survive a reload offline.
  async getUserSettings(): Promise<UserSettingsDoc | undefined> {
    try {
      const db = await this.openDB();
      const username = (await firstValueFrom(this.auth.username$))?.trim();
      if (!username) return undefined;
      const id = `user:${username}:settings`; // matches the key saveUserSettings() writes to
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.userSettingsStore], 'readonly');
        const store = transaction.objectStore(this.userSettingsStore);
        const request = store.get(id);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result as UserSettingsDoc);
      });
    } catch (error) {
      console.error('Error getting user settings:', error);
      return undefined;
    }
  }

  async searchNotes(query: string): Promise<Note[]> {
    try {
      const allNotes = await this.getAllNotes();
      const searchTerm = (query ?? '').toString().toLowerCase();
      return allNotes.filter((note: Note) => {
        const title = (note.Title ?? '').toString().toLowerCase();
        const noteType = (note.NoteType ?? '').toString().toLowerCase();
        return title.includes(searchTerm) || noteType.includes(searchTerm);
      });
    } catch (error) {
      console.error('Error searching notes:', error);
      return [];
    }
  }

  async getStats(): Promise<{ totalNotes: number; dbSize: number }> {
    try {
      const notes = await this.getAllNotes();
      return {
        totalNotes: notes.length,
        dbSize: notes.length, // Simplified size calculation
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      return { totalNotes: 0, dbSize: 0 };
    }
  }

  //#############################################################################
  // SYNCHRONISATION OPERATIONS
  //#############################################################################

// SIMPLE, SAFE, PERSISTENT FLAG
async hasBaselineData(): Promise<boolean> {
  const db = await this.openDB();
  return new Promise<boolean>((resolve) => {
    const tx = db.transaction([this.notesStore], 'readonly');
    const store = tx.objectStore(this.notesStore);
    const req = store.count();

    req.onsuccess = () => resolve(req.result > 0);
    req.onerror = () => resolve(false);
  });
}
  private readonly BASELINE_KEY = 'baseline_done';

async isBaselineDone(): Promise<boolean> {
  const db = await this.openDB();

  return new Promise<boolean>((resolve) => {
    const tx = db.transaction(['meta'], 'readonly');
    const store = tx.objectStore('meta');
    const req = store.get('baselineDone');

    req.onsuccess = () => {
      resolve(req.result?.value === true);
    };

    req.onerror = () => resolve(false);
  });
}

private readonly LAST_SYNC_TIME_KEY = 'lastSyncTime';

/** Persists the last successful sync completion time so "Last sync" survives
 * across app restarts/sessions instead of always starting at "never". */
async setLastSyncTime(time: Date): Promise<void> {
  const db = await this.openDB();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(['meta'], 'readwrite');
    const store = tx.objectStore('meta');
    const req = store.put({ key: this.LAST_SYNC_TIME_KEY, value: time.toISOString() });

    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async getLastSyncTime(): Promise<Date | null> {
  try {
    const db = await this.openDB();
    return new Promise<Date | null>((resolve) => {
      const tx = db.transaction(['meta'], 'readonly');
      const store = tx.objectStore('meta');
      const req = store.get(this.LAST_SYNC_TIME_KEY);

      req.onsuccess = () => {
        const raw = req.result?.value;
        const parsed = typeof raw === 'string' ? new Date(raw) : null;
        resolve(parsed && !isNaN(parsed.getTime()) ? parsed : null);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/** True when the attachments store holds anything (incl. pending uploads that exist only locally). */
async hasAnyAttachments(): Promise<boolean> {
  const db = await this.openDB();

  return new Promise<boolean>((resolve, reject) => {
    const tx = db.transaction([this.attachmentsStore], 'readonly');
    const req = tx.objectStore(this.attachmentsStore).count();
    req.onsuccess = () => resolve(req.result > 0);
    req.onerror = () => reject(req.error);
  });
}

async hasAnyNotes(): Promise<boolean> {
  const db = await this.openDB();

  return new Promise<boolean>((resolve, reject) => {
    const tx = db.transaction([this.notesStore], 'readonly');
    const store = tx.objectStore(this.notesStore);

    const req = store.count();

    req.onsuccess = () => {
      resolve(req.result > 0);
    };

    req.onerror = () => {
      reject(req.error);
    };
  });
}


  private baselineCompleted$ = new Subject<void>();
  private incrementalChangesApplied$ = new Subject<void>();
  private noteDirtied$ = new Subject<void>();
  private attachmentIdReplaced$ = new Subject<{ oldId: string; newId: string; noteId?: string }>();
  private attachmentsChanged$ = new Subject<void>();

  /** Something in the attachments store was added, changed or removed (NoteSizeService recounts). */
  onAttachmentsChanged(): Observable<void> {
    return this.attachmentsChanged$.asObservable();
  }

  /** A queued (offline / local-draft) attachment got its server id — see replaceAttachmentId(). Open
   * editors use it to repoint image nodes still referencing the temporary id. */
  onAttachmentIdReplaced(): Observable<{ oldId: string; newId: string; noteId?: string }> {
    return this.attachmentIdReplaced$.asObservable();
  }

  emitBaselineCompleted(): void {
    console.log('[OFFLINE] baseline completed → notifying DataService');
    this.baselineCompleted$.next();
  }

  onBaselineCompleted(): Observable<void> {
    return this.baselineCompleted$.asObservable();
  }

  emitIncrementalChangesApplied(): void {
    console.log('[OFFLINE] incremental changes applied → notifying DataService');
    this.incrementalChangesApplied$.next();
  }

  onIncrementalChangesApplied(): Observable<void> {
    return this.incrementalChangesApplied$.asObservable();
  }

  /** Emitted whenever a note is marked dirty by a local edit (see saveNote). Used by
   * SyncCoordinatorService to reset its adaptive polling interval back to the floor. */
  onNoteDirtied(): Observable<void> {
    return this.noteDirtied$.asObservable();
  }

  async syncNotesFromCouchDB(couchdbNotes: Note[]): Promise<void> {
    console.log(`[OFFLINE][BASELINE] syncing ${couchdbNotes.length} notes from CouchDB`);

    const db = await this.openDB();

    return new Promise<void>((resolve, reject) => {
      const tx = db.transaction([this.notesStore], 'readwrite');
      const store = tx.objectStore(this.notesStore);

      let written = 0;
      const syncTime = new Date().toISOString();

      // wipe store first (baseline is authoritative)
      const clearReq = store.clear();

      clearReq.onerror = () => reject(clearReq.error);

      clearReq.onsuccess = () => {
        if (couchdbNotes.length === 0) {
          resolve();
          return;
        }

        for (const note of couchdbNotes) {
          const parsedSize = Number(note.Size);
          const record: Note & {
            remote_rev: string;
            syncStatus: string;
            syncTime: string;
          } = {
            ...note,
            type: note.type || 'note',
            remote_rev: note._rev ?? '',
            syncStatus: 'synced',
            syncTime,
            Size: Number.isFinite(parsedSize) && parsedSize >= 0 ? parsedSize : 0,
          };

          const title = (record as any).Title ?? (record as any).title ?? (record as any).Name ?? '[NO TITLE]';

          const req = store.put(record);

          req.onsuccess = () => {
            written++;
            console.log(`[OFFLINE][BASELINE] ✔ wrote ${record._id} — ${title}`);
          };

          req.onerror = () => {
            console.error(`[OFFLINE][BASELINE] ✖ failed ${record._id}`, req.error);
            reject(req.error);
          };
        }
      };

      tx.oncomplete = () => {
        console.log(`[OFFLINE][BASELINE] TRANSACTION COMMIT OK — ${written}/${couchdbNotes.length}`);
        resolve();
      };

      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  async markNoteAsSynced(id: string): Promise<void> {
    console.log('...in markNoteAsSynced()..', id);

    const db = await this.openDB();
    const tx = db.transaction(this.notesStore, 'readwrite');
    const store = tx.objectStore(this.notesStore);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);
      getRequest.onerror = () => reject(new Error('Note record not found in IndexedDB'));

      getRequest.onsuccess = () => {
        const note = getRequest.result;
        if (!note) return reject(new Error('Note not found'));

        note.syncStatus = 'synced';
        note.syncTime = new Date().toISOString();

        const updateRequest = store.put(note);
        updateRequest.onerror = () => reject(new Error('Failed to update note status'));
        updateRequest.onsuccess = () => resolve();
      };
    });
  }

  /**
   * Turns the local copy into a deletion tombstone (syncStatus 'deleted') — tasks/delete_trash_client.md.
   * The note is hidden from every normal view straight away (see isPendingLocalDeletion()), and
   * SyncCoordinatorService pushes it as a DELETE (a move to Trash on the server) whenever it can,
   * so a delete made offline is never lost. Status and last_modified are left alone: deletion is
   * tracked by the server's own deleted/deletedAt fields, not by the note's content.
   * Resolves with the note as it was before, so a rejected delete can be undone (restoreLocalNote).
   */
  async markNoteAsDeleted(id: string): Promise<Note> {
    const db = await this.openDB();
    const tx = db.transaction(this.notesStore, 'readwrite');
    const store = tx.objectStore(this.notesStore);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);

      getRequest.onerror = () => reject(new Error(`Note with ID '${id}' not found`));
      getRequest.onsuccess = () => {
        const note = getRequest.result as Note | undefined;
        if (!note) return reject(new Error(`Note with ID '${id}' does not exist`));
        const previous: Note = { ...note };

        const updateRequest = store.put({ ...note, syncStatus: 'deleted' });
        updateRequest.onerror = () => reject(new Error('Failed to update note as deleted'));
        updateRequest.onsuccess = () => resolve(previous);
      };
    });
  }

  /** Writes a note back exactly as given (no syncStatus change) — undoes markNoteAsDeleted(). */
  async restoreLocalNote(note: Note): Promise<void> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.notesStore, 'readwrite');
      const request = tx.objectStore(this.notesStore).put(note);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async getDirtyNotes(): Promise<Note[]> {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.notesStore, 'readonly');
      const store = tx.objectStore(this.notesStore);
      const req = store.getAll();

      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const notes = (req.result as Note[]).filter((n) => n.syncStatus && n.syncStatus !== 'synced');
        resolve(notes);
      };
    });
  }

  async deleteNotes(noteIds: string[]): Promise<void> {
    const db = await this.openDB();
    const tx = db.transaction('notes', 'readwrite');
    const store = tx.objectStore('notes');

    for (const id of noteIds) {
      await store.delete(id);
    }

    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  async deleteNote(id: string): Promise<void> {
    try {
      console.log('Deleting offline Note, ID=', id);
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.notesStore], 'readwrite');
        const store = transaction.objectStore(this.notesStore);
        const request = store.delete(id);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  }

  async saveAttachmentMeta(meta: {
    _id: string;
    noteId: string;
    filename: string;
    mimeType?: string;
    isImage?: boolean;
    imageWidth?: number;
    imageHeight?: number;
    naturalWidth?: number;
    naturalHeight?: number;
    fileSizeBytes?: number;
    size?: number;
    modified_on?: string;
  }): Promise<void> {
    if (!meta?._id || !meta?.noteId) {
      return;
    }

    const existing = await this.getAttachmentRecord(meta._id);
    const now = new Date().toISOString();
    const next = {
      ...(existing || {}),
      _id: meta._id,
      noteId: meta.noteId,
      filename: meta.filename || existing?.filename || meta._id,
      mimeType: meta.mimeType || existing?.mimeType || DataOfflineService.DEFAULT_ATTACHMENT_MIME,
      isImage: meta.isImage === true ? true : existing?.isImage,
      imageWidth:
        Number.isFinite(Number(meta.imageWidth)) && Number(meta.imageWidth) > 0
          ? Number(meta.imageWidth)
          : existing?.imageWidth,
      imageHeight:
        Number.isFinite(Number(meta.imageHeight)) && Number(meta.imageHeight) > 0
          ? Number(meta.imageHeight)
          : existing?.imageHeight,
      naturalWidth:
        Number.isFinite(Number(meta.naturalWidth)) && Number(meta.naturalWidth) > 0
          ? Number(meta.naturalWidth)
          : existing?.naturalWidth,
      naturalHeight:
        Number.isFinite(Number(meta.naturalHeight)) && Number(meta.naturalHeight) > 0
          ? Number(meta.naturalHeight)
          : existing?.naturalHeight,
      fileSizeBytes:
        Number.isFinite(Number(meta.fileSizeBytes)) && Number(meta.fileSizeBytes) > 0
          ? Number(meta.fileSizeBytes)
          : existing?.fileSizeBytes,
      size: Number.isFinite(Number(meta.size)) ? Number(meta.size) : Number(existing?.size ?? 0),
      modified_on: meta.modified_on || existing?.modified_on || now,
      cached_on: now,
    };

    await this.putAttachmentRecord(next);
  }

  async saveAttachmentBlob(payload: {
    _id: string;
    noteId?: string;
    filename?: string;
    mimeType?: string;
    isImage?: boolean;
    imageWidth?: number;
    imageHeight?: number;
    naturalWidth?: number;
    naturalHeight?: number;
    fileSizeBytes?: number;
    size?: number;
    modified_on?: string;
    syncStatus?: 'synced' | 'pending-upload';
    blob: Blob;
  }): Promise<void> {
    if (!payload?._id || !payload?.blob) {
      return;
    }

    const existing = await this.getAttachmentRecord(payload._id);
    const now = new Date().toISOString();
    const next = {
      ...(existing || {}),
      _id: payload._id,
      noteId: payload.noteId || existing?.noteId || '',
      filename: payload.filename || existing?.filename || payload._id,
      mimeType: payload.mimeType || payload.blob.type || existing?.mimeType || DataOfflineService.DEFAULT_ATTACHMENT_MIME,
      isImage: payload.isImage === true ? true : existing?.isImage,
      imageWidth:
        Number.isFinite(Number(payload.imageWidth)) && Number(payload.imageWidth) > 0
          ? Number(payload.imageWidth)
          : existing?.imageWidth,
      imageHeight:
        Number.isFinite(Number(payload.imageHeight)) && Number(payload.imageHeight) > 0
          ? Number(payload.imageHeight)
          : existing?.imageHeight,
      naturalWidth:
        Number.isFinite(Number(payload.naturalWidth)) && Number(payload.naturalWidth) > 0
          ? Number(payload.naturalWidth)
          : existing?.naturalWidth,
      naturalHeight:
        Number.isFinite(Number(payload.naturalHeight)) && Number(payload.naturalHeight) > 0
          ? Number(payload.naturalHeight)
          : existing?.naturalHeight,
      fileSizeBytes:
        Number.isFinite(Number(payload.fileSizeBytes)) && Number(payload.fileSizeBytes) > 0
          ? Number(payload.fileSizeBytes)
          : existing?.fileSizeBytes,
      size:
        Number.isFinite(Number(payload.size)) && Number(payload.size) > 0
          ? Number(payload.size)
          : Number(payload.blob.size || existing?.size || 0),
      modified_on: payload.modified_on || existing?.modified_on || now,
      cached_on: now,
      syncStatus: payload.syncStatus ?? existing?.syncStatus ?? 'synced',
      blob: payload.blob,
    };

    await this.putAttachmentRecord(next);
  }

  async getAttachmentMeta(id: string): Promise<{
    _id: string;
    noteId: string;
    filename: string;
    mimeType: string;
    isImage?: boolean;
    imageWidth?: number;
    imageHeight?: number;
    naturalWidth?: number;
    naturalHeight?: number;
    fileSizeBytes?: number;
    size: number;
    modified_on?: string;
    cached_on?: string;
    hasBlob: boolean;
  } | undefined> {
    const row = await this.getAttachmentRecord(id);
    if (!row?._id) {
      return undefined;
    }

    return {
      _id: String(row._id),
      noteId: String(row.noteId ?? ''),
      filename: String(row.filename ?? row._id ?? ''),
      mimeType: String(row.mimeType ?? DataOfflineService.DEFAULT_ATTACHMENT_MIME),
      isImage: row?.isImage === true ? true : undefined,
      imageWidth:
        Number.isFinite(Number(row?.imageWidth)) && Number(row?.imageWidth) > 0
          ? Number(row.imageWidth)
          : undefined,
      imageHeight:
        Number.isFinite(Number(row?.imageHeight)) && Number(row?.imageHeight) > 0
          ? Number(row.imageHeight)
          : undefined,
      naturalWidth:
        Number.isFinite(Number(row?.naturalWidth)) && Number(row?.naturalWidth) > 0
          ? Number(row.naturalWidth)
          : undefined,
      naturalHeight:
        Number.isFinite(Number(row?.naturalHeight)) && Number(row?.naturalHeight) > 0
          ? Number(row.naturalHeight)
          : undefined,
      fileSizeBytes:
        Number.isFinite(Number(row?.fileSizeBytes)) && Number(row?.fileSizeBytes) > 0
          ? Number(row.fileSizeBytes)
          : undefined,
      size: Number(row.size ?? 0),
      modified_on: row.modified_on,
      cached_on: row.cached_on,
      hasBlob: !!row.blob,
    };
  }

  async getPendingAttachmentUploads(): Promise<Array<{
    _id: string;
    noteId: string;
    filename: string;
    mimeType: string;
    size: number;
    blob: Blob;
  }>> {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.attachmentsStore], 'readonly');
      const store = tx.objectStore(this.attachmentsStore);
      const req = store.getAll();

      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const rows = Array.isArray(req.result) ? req.result : [];
        resolve(
          rows
            .filter((row: any) => row?.syncStatus === 'pending-upload' && !!row?.blob)
            .map((row: any) => ({
              _id: String(row._id),
              noteId: String(row.noteId ?? ''),
              filename: String(row.filename ?? row._id),
              mimeType: String(row.mimeType ?? DataOfflineService.DEFAULT_ATTACHMENT_MIME),
              size: Number(row.size ?? 0),
              blob: row.blob as Blob,
            }))
        );
      };
    });
  }

  async replaceAttachmentId(
    oldId: string,
    newRecord: {
      _id: string;
      noteId?: string;
      filename?: string;
      mimeType?: string;
      size?: number;
      blob?: Blob;
    },
  ): Promise<void> {
    if (!oldId || !newRecord?._id) {
      return;
    }

    const existing = await this.getAttachmentRecord(oldId);
    const now = new Date().toISOString();
    const merged = {
      ...(existing || {}),
      ...newRecord,
      syncStatus: 'synced' as const,
      cached_on: now,
    };

    await this.putAttachmentRecord(merged);
    if (oldId !== newRecord._id) {
      await this.deleteAttachment(oldId);
      this.attachmentIdReplaced$.next({ oldId, newId: newRecord._id, noteId: newRecord.noteId });
    }
  }

  async getAttachmentBlob(id: string): Promise<Blob | undefined> {
    const existing = await this.getAttachmentRecord(id);
    if (!existing?.blob) {
      return undefined;
    }
    return existing.blob as Blob;
  }

  async getAttachmentMetasForNote(noteId: string): Promise<Array<{
    _id: string;
    noteId: string;
    filename: string;
    mimeType: string;
    isImage?: boolean;
    imageWidth?: number;
    imageHeight?: number;
    naturalWidth?: number;
    naturalHeight?: number;
    fileSizeBytes?: number;
    size: number;
    modified_on?: string;
    cached_on?: string;
    hasBlob: boolean;
    syncStatus?: string;
  }>> {
    const db = await this.openDB();

    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.attachmentsStore], 'readonly');
      const store = tx.objectStore(this.attachmentsStore);
      const index = store.index('noteId');
      const req = index.getAll(noteId);

      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const rows = Array.isArray(req.result) ? req.result : [];
        resolve(
          rows.map((row: any) => ({
            _id: String(row?._id ?? ''),
            noteId: String(row?.noteId ?? noteId),
            filename: String(row?.filename ?? row?._id ?? ''),
            mimeType: String(row?.mimeType ?? DataOfflineService.DEFAULT_ATTACHMENT_MIME),
            isImage: row?.isImage === true ? true : undefined,
            imageWidth:
              Number.isFinite(Number(row?.imageWidth)) && Number(row?.imageWidth) > 0
                ? Number(row?.imageWidth)
                : undefined,
            imageHeight:
              Number.isFinite(Number(row?.imageHeight)) && Number(row?.imageHeight) > 0
                ? Number(row?.imageHeight)
                : undefined,
            naturalWidth:
              Number.isFinite(Number(row?.naturalWidth)) && Number(row?.naturalWidth) > 0
                ? Number(row?.naturalWidth)
                : undefined,
            naturalHeight:
              Number.isFinite(Number(row?.naturalHeight)) && Number(row?.naturalHeight) > 0
                ? Number(row?.naturalHeight)
                : undefined,
            fileSizeBytes:
              Number.isFinite(Number(row?.fileSizeBytes)) && Number(row?.fileSizeBytes) > 0
                ? Number(row?.fileSizeBytes)
                : undefined,
            size: Number(row?.size ?? 0),
            modified_on: row?.modified_on,
            cached_on: row?.cached_on,
            hasBlob: !!row?.blob,
            syncStatus: row?.syncStatus,
          }))
        );
      };
    });
  }

  async deleteAttachment(id: string): Promise<void> {
    if (!id) {
      return;
    }
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.attachmentsStore], 'readwrite');
      const store = tx.objectStore(this.attachmentsStore);
      const req = store.delete(id);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        this.attachmentsChanged$.next();
        resolve();
      };
    });
  }

  /** Total cached attachment bytes per note (note id → bytes), counting each attachment once. */
  async getAttachmentBytesByNote(): Promise<Map<string, number>> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.attachmentsStore], 'readonly');
      const req = tx.objectStore(this.attachmentsStore).getAll();
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const totals = new Map<string, number>();
        for (const row of Array.isArray(req.result) ? req.result : []) {
          const noteId = String(row?.noteId ?? '');
          if (!noteId) continue;
          const candidates = [row?.size, row?.fileSizeBytes, row?.blob?.size].map(Number);
          const bytes = candidates.find((n) => Number.isFinite(n) && n > 0) ?? 0;
          totals.set(noteId, (totals.get(noteId) ?? 0) + bytes);
        }
        resolve(totals);
      };
    });
  }

  async deleteAttachmentsByNote(noteId: string): Promise<void> {
    if (!noteId) {
      return;
    }
    const rows = await this.getAttachmentMetasForNote(noteId);
    if (!rows.length) {
      return;
    }
    await Promise.all(rows.map((row) => this.deleteAttachment(row._id)));
  }

  async pruneNoteAttachments(noteId: string, keepAttachmentIds: string[]): Promise<void> {
    const keep = new Set((keepAttachmentIds || []).filter((id) => !!id));
    const rows = await this.getAttachmentMetasForNote(noteId);
    // A queued upload (offline / local draft) exists only here and isn't on the server yet — keep it.
    const toDelete = rows.filter((row) => !keep.has(row._id) && row.syncStatus !== 'pending-upload');
    if (!toDelete.length) {
      return;
    }
    await Promise.all(toDelete.map((row) => this.deleteAttachment(row._id)));
  }

  private async getAttachmentRecord(id: string): Promise<any | undefined> {
    if (!id) {
      return undefined;
    }

    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.attachmentsStore], 'readonly');
      const store = tx.objectStore(this.attachmentsStore);
      const req = store.get(id);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
    });
  }

  private async putAttachmentRecord(record: any): Promise<void> {
    const db = await this.openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([this.attachmentsStore], 'readwrite');
      const store = tx.objectStore(this.attachmentsStore);
      const req = store.put(record);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        this.attachmentsChanged$.next();
        resolve();
      };
    });
  }

  async clearAll(): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.notesStore], 'readwrite');
        const store = transaction.objectStore(this.notesStore);
        const request = store.clear();

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    } catch (error) {
      console.error('Error clearing notes:', error);
      throw error;
    }
  }

  /** Public: Notes info from IndexedDB, ApiResponse-shaped */
  async getNotesInfoLocal(): Promise<ApiResponse<DbInfo>> {
    return this.getStoreInfoLocal('notes');
  }

  /** Fire-and-forget: fetch local counts and log to "synch" */
  async fetchDbInfoAndLogActive(): Promise<void> {
    try {
      const notes = await this.getNotesInfoLocal();

      if (notes.status === 'success' && notes.data) {
        //this.logs.append('synch', 'Notes active in offline IndexDB:', String(notes.data.active));
        //this.logs.append('synch', 'Notes total in offline IndexDB:', String(notes.data.total));
      } else {
        this.logs.append('synch', 'IndexedDB Notes info warning:', notes.error?.message ?? 'Unknown error');
      }
    } catch (e: any) {
      this.logs.append('synch', 'IndexedDB info retrieval failed:', e?.message ?? String(e));
    }
  }

  // ---------- Internals ----------

  private async getStoreInfoLocal(storeName: string): Promise<ApiResponse<DbInfo>> {
    try {
      const db = await this.openDB();
      const counts = await this.countStore(db, storeName);

      const info: DbInfo = {
        db_name: storeName,
        total: counts.total,
        active: counts.active,
        deleted_status: counts.deleted_status,
        tombstones: 0, // IndexedDB has no tombstones
        update_seq: 0,
        generated_at: new Date().toISOString(),
        status_field_present: counts.status_field_present,
      };

      return { status: 'success', data: info };
    } catch (err: any) {
      return {
        status: 'error',
        error: { code: 0, message: err?.message ?? String(err) },
      };
    }
  }

  /**
   * Count totals and logical deletes for a given store.
   * Uses Status index if present; falls back to cursor scan if not.
   */
  private countStore(
    db: IDBDatabase,
    storeName: string,
  ): Promise<{
    total: number;
    deleted_status: number;
    active: number;
    status_field_present: boolean;
  }> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);

      const totalReq = store.count();
      totalReq.onerror = () => reject(totalReq.error);

      totalReq.onsuccess = () => {
        const total = Number(totalReq.result || 0);

        const hasStatusIndex = Array.from(store.indexNames).includes('Status');
        if (hasStatusIndex) {
          // Fast path via index
          const idx = store.index('Status');
          const delReq = idx.count('Deleted');
          delReq.onerror = () => reject(delReq.error);
          delReq.onsuccess = () => {
            const deleted_status = Number(delReq.result || 0);
            // If there's an index, we can also infer presence of Status field (not perfect, but indicative)
            const status_field_present = true;
            resolve({
              total,
              deleted_status,
              active: Math.max(0, total - deleted_status),
              status_field_present,
            });
          };
        } else {
          // Fallback: full scan to find Status === 'Deleted' and detect presence of Status
          let deleted_status = 0;
          let status_field_present = false;

          const cursorReq = store.openCursor();
          cursorReq.onerror = () => reject(cursorReq.error);
          cursorReq.onsuccess = (e: any) => {
            const cursor: IDBCursorWithValue | null = e.target.result;
            if (!cursor) {
              resolve({
                total,
                deleted_status,
                active: Math.max(0, total - deleted_status),
                status_field_present,
              });
              return;
            }
            const v = cursor.value;
            if (v && 'Status' in v) status_field_present = true;
            if (v?.Status === 'Deleted') deleted_status++;
            cursor.continue();
          };
        }
      };
    });
  }

  //#############################################################################
  // CREATE / UPDATE / DELETE OPERATIONS
  //#############################################################################

  private generateUuid(): string {
    if (crypto && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = crypto.getRandomValues(new Uint8Array(1))[0] & 15;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  generateId(): string {
    return `note:${this.generateUuid()}`;
  }

  private generateTemplateId(): string {
    return `template:${this.generateUuid()}`;
  }

  /** Computes the note's serialized byte size, excluding the Size field itself. */
  private computeNoteSizeBytes(note: Note): number {
    const { Size: _omit, ...rest } = note as any;
    return new Blob([JSON.stringify(rest)]).size;
  }

  /** `keepLastModified` (local saves only): a tag-only change — last_modified stays as it is, and
   * the push keeps it too (Note.keepLastModified), unless a real edit is already waiting to be pushed. */
  async saveNote(note: Note, syncMode: boolean, options: { keepLastModified?: boolean } = {}): Promise<void> {
    try {
      const db = await this.openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.notesStore], 'readwrite');
        const store = transaction.objectStore(this.notesStore);
        if (!note._id) {
          const fallbackId = (note as any).id || (note as any).note_id || (note as any).doc_id;
          if (fallbackId) {
            note._id = fallbackId;
          }
        }
        if (!note._id) {
          reject(new Error('Cannot save note: missing _id'));
          return;
        }
        if (!note.type) {
          note.type = 'note';
        }
        // If syncMode is true, set syncStatus to 'synced', otherwise set it to 'updated'
        if (syncMode) {
          delete note.keepLastModified;
          note.syncStatus = 'synced';
          if (note._rev) {
            note.remote_rev = note._rev;
          }
          // Server/sync data is authoritative for Size; only fill in a default
          // when the incoming doc doesn't carry one (e.g. legacy notes).
          const syncedSize = Number(note.Size);
          note.Size = Number.isFinite(syncedSize) && syncedSize >= 0 ? syncedSize : 0;
        } else {
          if (options.keepLastModified) {
            // Only when nothing else is waiting to be pushed: a pending edit still gets its new date.
            if (!note.syncStatus || note.syncStatus === 'synced' || note.keepLastModified) note.keepLastModified = true;
          } else {
            delete note.keepLastModified;
            note.last_modified = new Date().toISOString();
          }
          if (note.syncStatus !== 'new') note.syncStatus = 'updated';
          this.noteDirtied$.next();
          // Recompute locally so Size is fresh for offline-first saves; the
          // remote round-trip (if any) will overwrite it with the server's value.
          note.Size = this.computeNoteSizeBytes(note);
        }
        note.syncTime = new Date().toISOString();
        // If _id is not set, generate a new one
        const request = store.put(note);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    } catch (error) {
      console.error('Error saving note:', error);
      throw error;
    }
  }

  async saveUserSettings(userSettings: UserSettingsDoc, syncMode: boolean): Promise<void> {
    try {
      const db = await this.openDB();

      // get the string value from the Observable
      const username = (await firstValueFrom(this.auth.username$))?.trim();
      if (!username) throw new Error('Username missing when saving user settings');

      // choose your suffix; keep it consistent across app ("settings" vs "setting")
      const id = `user:${username}:settings`; // or "...:setting" if that’s your canonical key
      userSettings._id = id;

      return new Promise((resolve, reject) => {
        const tx = db.transaction([this.userSettingsStore], 'readwrite');
        const store = tx.objectStore(this.userSettingsStore);

        const req = store.put(userSettings);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch (error) {
      console.error('Error saving user-settings:', error);
      throw error;
    }
  }

  async createOfflineNote(syncMode: boolean): Promise<{ message?: string; _id?: string; data?: Note; error?: string }> {
    try {
      const db = await this.openDB();

      const now = new Date().toISOString();
      const newNote: Note = {
        _id: this.generateId(),
        type: 'note',
        NoteType: 'To Do',
        Title: 'New Note',
        Project: 2,
        ProjectName: 'At Home',
        Status: DEFAULT_NOTE_STATUS,
        ReferenceDate: now,
        Confidential: false,
        // Description: "",
        // Comments: "",
        // Rich text fields — canonical Delta + derived HTML
        DescriptionDelta: new Delta(),
        DescriptionHtml: '',
        CommentsDelta: new Delta(),
        CommentsHtml: '',
        Keywords: '',
        AiKeywords: '',
        created_on: now,
        last_modified: now,
        syncTime: now,
        syncStatus: syncMode ? 'synced' : 'new',
      };

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.notesStore], 'readwrite');
        const store = transaction.objectStore(this.notesStore);
        const request = store.add(newNote);

        request.onerror = () => {
          resolve({ error: 'Failed to create note' });
        };

        request.onsuccess = () => {
          resolve({
            message: 'Note created successfully',
            _id: newNote._id,
            data: newNote,
          });
        };
      });
    } catch (error) {
      console.error('Error creating offline note:', error);
      return { error: 'Failed to create note' };
    }
  }

  // 30.Aug.2025    TO DO: Implement user-settings store operations if needed
  async clearIndexedDbStores(): Promise<void> {
    const db = await this.openDB();

    return new Promise<void>((resolve, reject) => {
      try {
        const tx = db.transaction([this.notesStore, this.attachmentsStore], 'readwrite');
        const notes = tx.objectStore(this.notesStore);
        const attachments = tx.objectStore(this.attachmentsStore);
        // ?? Need to decide if we should also clear user-settings store ??

        const req1 = notes.clear();
        const req3 = attachments.clear();

        req1.onerror = () => reject(req1.error);
        req3.onerror = () => reject(req3.error);

        tx.oncomplete = () => {
          this.logs.append('synch', 'IndexedDB: cleared stores "notes" and "attachments".');
          this.attachmentsChanged$.next();
          resolve();
        };
        tx.onerror = () => reject(new Error('Failed to clear stores'));
        tx.onabort = () => reject(new Error('Transaction aborted while clearing stores'));
      } catch (e: any) {
        reject(e);
      }
    });
  }

  /** Clears *all* localStorage keys (or only those with a prefix) */
  clearLocalStorage(prefix?: string): void {
    if (!prefix) {
      localStorage.clear();
      this.logs.append('synch', 'localStorage: cleared all keys.');
      return;
    }
    // selective clear by prefix
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
    this.logs.append('synch', `localStorage: cleared keys with prefix "${prefix}".`);
  }

  /** One-call helper */
  async clearOfflineAndLocal(prefix?: string): Promise<void> {
    await this.clearIndexedDbStores();
    this.clearLocalStorage(prefix);
  }

  //#############################################################################
  // OBSOLETE CODE SECTION !!!
  //#############################################################################
}
